import { useEffect, useRef, useState } from "react";
import {
  DOSE_REASON_LABELS,
  type DoseLogEntryView,
  type DoseLogView,
} from "@care-by-ella/contracts";
import {
  correctFirstDose,
  fetchDoseLog,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-1151 S7 (Ö-423, Ö-424; Emelie 2026-10-15) — her dose log on the
// treatment overview, next to the dose schedule. Read only: doses are
// patient-reported and staff never register or change one. Doctor only
// ("Coachen ska inte få direkt åtkomst"); the route answers 403 to anyone
// else, and every read is PDL-logged. The two statuses Ö-424 keeps apart:
// "Ingen dos tagen" (her registration) and "Uppehåll enligt läkare" (a
// doctor's pause). Neither changes the prescription. The staff-facing
// strings are Emelie's approved copy (Ö-672, 2026-10-20).
//
// TES-1455 (Ö-687) — "… och därefter via ansvarig läkare": the one thing the
// doctor changes here is her first dose's date, as a new version that keeps
// the earlier ones, audited.
//
// TES-1522 (Ö-789 b, Ö-807; Emelie 2026-10-21) — the correction recomputes
// her future planned dose days and check-ins from the corrected date; for a
// new treatment it also moves the treatment day, for a transfer it leaves
// her historical start alone. The field, the explanation and the refusals
// are Emelie's Ö-807 sentences, verbatim.

function asDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

function formatDay(iso: string): string {
  return asDay(iso).toLocaleDateString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatInstant(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

// TES-1491 (Ö-672) — the approved staff copy splits the first dose by her
// patient group.
function statusOf(
  e: DoseLogEntryView,
  firstDoseKind: DoseLogView["firstDoseKind"],
): string {
  if (e.kind === "first_dose") {
    return firstDoseKind === "transfer"
      ? "Första dosen hos Ella"
      : "Första dosen i behandlingen";
  }
  return e.outcome === "taken" ? "Dosen är tagen" : "Ingen dos tagen";
}

function detailOf(e: DoseLogEntryView): string[] {
  const out: string[] = [];
  if (e.takenOn) {
    out.push(
      `Tagen ${formatDay(e.takenOn)}${e.takenAtTime ? ` kl. ${e.takenAtTime}` : ""}`,
    );
  }
  if (e.plannedOn && e.timing !== "on_day") {
    const when =
      e.timing === "late"
        ? " (senare än planerat)"
        : e.timing === "early"
          ? " (tidigare än planerat)"
          : "";
    out.push(`Planerad ${formatDay(e.plannedOn)}${when}`);
  }
  if (e.doseReport === "different") {
    out.push(
      `Annan dos, patientrapporterad: ${e.reportedDose ?? "ej angiven"}`,
    );
  }
  // TES-1151 S6 (Ö-415) — her optional reason, in her app's words.
  if (e.reason) {
    out.push(
      `Orsak: ${DOSE_REASON_LABELS[e.reason]}${e.reasonNote ? ` – ”${e.reasonNote}”` : ""}`,
    );
  }
  const ordination = [e.prescribed.medication, e.prescribed.dose]
    .filter(Boolean)
    .join(" ");
  if (ordination) out.push(`Ordination då: ${ordination}`);
  out.push(`Registrerad ${formatInstant(e.reportedAt)}`);
  // TES-1151 S8 (Ö-417) — the answers it replaced, oldest first. Each was
  // replaced by the next version, whose author is who corrected it: she
  // within 24 hours, or the doctor on a first dose (TES-1455).
  const versions = e.corrections ?? [];
  versions.forEach((c, i) => {
    const was =
      e.kind === "first_dose"
        ? c.takenOn
          ? formatDay(c.takenOn)
          : "okänt datum"
        : c.outcome === "taken"
          ? `Dosen är tagen${c.takenOn ? ` ${formatDay(c.takenOn)}` : ""}`
          : "Ingen dos tagen";
    const by = (versions[i + 1] ?? e).reportedBy;
    out.push(
      `${by === "doctor" ? "Rättad av läkaren" : "Rättad av patienten"}, tidigare: ${was}`,
    );
  });
  return out;
}

// Ö-807's refusals for the first-dose correction; the start bound is
// TES-1489's sentence ("cirka" when the doctor estimated the start).
function saveErrorText(e: unknown): string {
  if (!(e instanceof Error)) return "Datumet kunde inte sparas. Försök igen.";
  const params = (e as { messageParams?: { date?: unknown } }).messageParams;
  const date =
    typeof params?.date === "string" ? formatLongDay(params.date) : null;
  const key = (e as { messageKey?: unknown }).messageKey;
  if (e.message === "dose_date_in_future") {
    return "Datumet kan inte ligga efter i dag.";
  }
  if (e.message === "dose_date_before_treatment_start" && date) {
    return key === "errors.treatment.dose_date_before_estimated_treatment_start"
      ? `Dosdatumet kan inte vara före din behandlingsstart, cirka ${date}. Kontrollera datumet.`
      : `Dosdatumet kan inte vara före din behandlingsstart, ${date}. Kontrollera datumet.`;
  }
  if (e.message === "first_dose_not_before_later_dose" && date) {
    return `Första dosen med Ella som vårdgivare måste ligga före nästa registrerade dos, ${date}. Kontrollera datumet.`;
  }
  return "Datumet kunde inte sparas. Försök igen.";
}

function formatLongDay(iso: string): string {
  return asDay(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function DoseLogPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const [log, setLog] = useState<DoseLogView | null>(null);
  const [loadError, setLoadError] = useState(false);
  // TES-1455 — the first dose's date being corrected, or null.
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // TES-1040 — the admin's support view shows the log, never the correction.
  const readOnly = useReadOnly();
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    fetchDoseLog(patientId)
      .then((v) => {
        if (live) setLog(v);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        setLoadError(true);
      });
    return () => {
      live = false;
    };
  }, [patientId]);

  if (loadError) {
    return (
      <p className="text-xs text-textSecondary">
        Dosloggen kunde inte läsas. Ladda om sidan.
      </p>
    );
  }
  if (!log) return null;

  const saveFirstDose = async () => {
    if (!correcting) return;
    setBusy(true);
    setSaveError(null);
    try {
      setLog(await correctFirstDose(patientId, correcting));
      setCorrecting(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorizedRef.current();
        return;
      }
      // TES-1489 (Ö-807): the first prescription is no longer a bound.
      setSaveError(saveErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const firstDoseCorrection = (e: DoseLogEntryView) =>
    correcting === null ? (
      <button
        type="button"
        onClick={() => {
          setCorrecting(e.takenOn ?? "");
          setSaveError(null);
        }}
        className="self-start text-xs text-textSecondary underline"
      >
        Rätta datum för första dosen
      </button>
    ) : (
      <div className="mt-xs flex flex-col gap-xs rounded-md border border-surfaceMuted p-sm">
        <label className="flex flex-col gap-xs text-xs">
          <span>
            {log.firstDoseKind === "transfer"
              ? "Datum för första dosen hos Ella"
              : "Datum för första dosen i behandlingen"}
          </span>
          <input
            type="date"
            value={correcting}
            onChange={(ev) => setCorrecting(ev.target.value)}
            disabled={busy}
            className="self-start rounded-md border border-surfaceMuted px-sm py-xs text-sm"
          />
        </label>
        <p className="text-xs text-textSecondary">
          {`Det tidigare datumet sparas i historiken. Framtida planerade dosdagar och incheckningar räknas om från det rättade datumet. ${
            log.firstDoseKind === "transfer"
              ? "Historisk behandlingsstart och total behandlingsdag ändras inte."
              : "När datumet också är behandlingsstart uppdateras behandlingsdagen."
          }`}
        </p>
        <div className="flex flex-wrap items-center gap-sm">
          <button
            type="button"
            onClick={() => void saveFirstDose()}
            disabled={busy || !correcting || correcting === e.takenOn}
            className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
          >
            Spara datum
          </button>
          <button
            type="button"
            onClick={() => setCorrecting(null)}
            disabled={busy}
            className="text-sm text-textSecondary underline"
          >
            Avbryt
          </button>
        </div>
        {saveError ? (
          <p className="text-xs text-danger" role="alert">
            {saveError}
          </p>
        ) : null}
      </div>
    );
  if (log.entries.length === 0 && log.pauses.length === 0) {
    return (
      <p className="text-sm text-textSecondary">Inga dosregistreringar ännu.</p>
    );
  }

  return (
    <div className="flex flex-col gap-sm text-sm">
      <ul className="flex flex-col gap-xs">
        {log.entries.map((e) => (
          <li key={e.id} className="flex flex-col">
            <span>{statusOf(e, log.firstDoseKind)}</span>
            <span className="text-xs text-textSecondary">
              {detailOf(e).join(" · ")}
            </span>
            {e.kind === "first_dose" && !readOnly
              ? firstDoseCorrection(e)
              : null}
          </li>
        ))}
      </ul>
      {log.pauses.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <h3 className="text-xs font-medium text-textSecondary">
            Uppehåll enligt läkare
          </h3>
          <ul className="flex flex-col gap-xs text-xs">
            {log.pauses.map((p, i) => (
              <li key={`${p.from ?? ""}:${i}`}>
                {`${p.from ? formatInstant(p.from) : "Okänd start"} – ${
                  p.to ? formatInstant(p.to) : "pågår"
                }`}
                {p.endedBy === "ended" ? " · behandlingen avslutades" : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="text-xs text-textSecondary">
        Dosregistreringarna är patientrapporterade. En registrering ändrar
        aldrig ordinationen.
      </p>
    </div>
  );
}
